import axios from 'axios';
import { ref } from 'vue';
import { isValidationError } from '@/js/account/shared/api';

function readErrorMessage(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return null;
  }

  const message: unknown = Reflect.get(data, 'message');
  return typeof message === 'string' ? message : null;
}

export function useFormErrors() {
  const errors = ref<Record<string, string[]>>({});
  const generalError = ref<string | null>(null);

  function clearErrors(): void {
    errors.value = {};
    generalError.value = null;
  }

  function setFromAxiosError(error: unknown): void {
    clearErrors();

    if (isValidationError(error)) {
      errors.value = error.response?.data.errors ?? {};
      generalError.value = error.response?.data.message ?? null;
      return;
    }

    if (axios.isAxiosError(error)) {
      if (error.response?.status === 419) {
        generalError.value
          = 'Your session has expired. Please refresh the page and try again.';
        return;
      }

      const message = readErrorMessage(error.response?.data);
      generalError.value = message ?? 'Something went wrong. Please try again.';
    }
  }

  function fieldError(name: string): string | undefined {
    if (!(name in errors.value)) {
      return undefined;
    }

    return errors.value[name][0];
  }

  function hasFieldError(name: string): boolean {
    return fieldError(name) !== undefined;
  }

  function clearFieldError(name: string): void {
    if (!(name in errors.value)) {
      return;
    }

    const next = { ...errors.value };
    delete next[name];
    errors.value = next;
  }

  function clearFieldOnInput(
    name: string,
    options?: { clearGeneral?: boolean },
  ): void {
    clearFieldError(name);

    if (options?.clearGeneral) {
      generalError.value = null;
    }
  }

  function setGeneralError(message: string): void {
    errors.value = {};
    generalError.value = message;
  }

  return {
    errors,
    generalError,
    clearErrors,
    clearFieldError,
    clearFieldOnInput,
    setFromAxiosError,
    setGeneralError,
    fieldError,
    hasFieldError,
  };
}
